import { useEffect, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { Share2, MessageCircle, Link as LinkIcon, FileDown, Check, ExternalLink, X, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/App";

// Same deny-list used on the guest page — defence-in-depth so the PDF
// (which we generate client-side) can never accidentally include cost data.
const FORBIDDEN_KEYS = new Set([
  "total_cost",
  "cost_per_serving",
  "target_margin",
  "plate_profit_percent",
  "unit_cost",
  "bulk_price",
  "bulk_quantity",
  "revenue_contribution",
  "subscription_tier",
  "recipe_count",
  "is_super_admin",
  "mobile_number",
  "phone",
]);

const scrub = (node) => {
  if (Array.isArray(node)) return node.map(scrub);
  if (node && typeof node === "object") {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      if (FORBIDDEN_KEYS.has(k)) continue;
      out[k] = scrub(v);
    }
    return out;
  }
  return node;
};

const formatPrice = (amount, symbol, position) => {
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return "";
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return position === "suffix" ? `${formatted} ${symbol}` : `${symbol}${formatted}`;
};

// Generate the floor-staff PDF. Guest-safe fields only — no cost, no margin.
// If qrDataUrl is provided, embeds it in the header as a live bridge to the
// digital menu (guests can scan from the printed sheet).
const generateStaffPdf = (menu, restaurantName, guestUrl, qrDataUrl, t) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 48;
  let y = 56;

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // HEADER — name + meta on the left, QR on the right
  const qrSize = 90;
  const headerTextW = pageW - marginX * 2 - (qrDataUrl ? qrSize + 16 : 0);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  const nameLines = doc.splitTextToSize(restaurantName || t("shareMenu.staffMenuTitle"), headerTextW);
  doc.text(nameLines, marginX, y);
  y += nameLines.length * 22;

  doc.setDrawColor(184, 134, 11); // gold
  doc.setLineWidth(1);
  doc.line(marginX, y - 10, marginX + 80, y - 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(t("shareMenu.staffMenuSubtitle", { date: today }), marginX, y);
  y += 12;
  doc.text(t("shareMenu.staffMenuCopy"), marginX, y);
  y += 12;
  doc.text(t("shareMenu.staffMenuNoCost"), marginX, y);
  y += 6;

  // QR block (top-right)
  if (qrDataUrl) {
    const qrX = pageW - marginX - qrSize;
    const qrY = 48;
    doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(80);
    doc.text(t("shareMenu.scanForLiveMenu"), qrX + qrSize / 2, qrY + qrSize + 10, {
      align: "center",
    });
  }

  // Move below whichever is taller
  y = Math.max(y + 14, 48 + qrSize + 28);
  doc.setTextColor(40);

  const ensureSpace = (needed) => {
    if (y + needed > pageH - 60) {
      doc.addPage();
      y = 56;
    }
  };

  menu.categories.forEach((cat) => {
    ensureSpace(40);
    // Category header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(184, 134, 11);
    doc.text(cat.name.toUpperCase(), marginX, y);
    doc.setDrawColor(184, 134, 11);
    doc.setLineWidth(0.5);
    doc.line(marginX, y + 4, pageW - marginX, y + 4);
    y += 22;
    doc.setTextColor(40);

    cat.items.forEach((item) => {
      ensureSpace(80);
      // Item name + price line
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      const price = formatPrice(item.price, item.currency_symbol, item.currency_position);
      const priceW = doc.getTextWidth(price);
      doc.text(item.name, marginX, y);
      doc.text(price, pageW - marginX - priceW, y);
      y += 14;

      // Dept tag (e.g. "Kitchen")
      if (item.department) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(130);
        doc.text(item.department.toUpperCase(), marginX, y);
        y += 12;
        doc.setTextColor(40);
      }

      // Notes
      if (item.notes) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(item.notes, pageW - marginX * 2);
        ensureSpace(lines.length * 12 + 4);
        doc.text(lines, marginX, y);
        y += lines.length * 12 + 4;
      }

      // Ingredients (allergen reference)
      if (item.ingredients && item.ingredients.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(t("shareMenu.ingredientsHeader"), marginX, y);
        y += 10;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(50);
        const ingLine = item.ingredients
          .map((ing) => {
            if (!ing.qty) return ing.name;
            return `${ing.name} · ${ing.qty} ${ing.unit || ""}`.trim();
          })
          .join("   ");
        const lines = doc.splitTextToSize(ingLine, pageW - marginX * 2);
        ensureSpace(lines.length * 11 + 4);
        doc.text(lines, marginX, y);
        y += lines.length * 11 + 4;
      }

      // Method (kitchen)
      if (item.method) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(t("shareMenu.methodHeader"), marginX, y);
        y += 10;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(50);
        const methodLines = doc.splitTextToSize(item.method, pageW - marginX * 2);
        ensureSpace(methodLines.length * 11 + 4);
        doc.text(methodLines, marginX, y);
        y += methodLines.length * 11 + 6;
      }

      y += 10;
      doc.setDrawColor(220);
      doc.setLineWidth(0.3);
      doc.line(marginX, y, pageW - marginX, y);
      y += 14;
      doc.setTextColor(40);
    });

    y += 10;
  });

  // FOOTER on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Prices in ${menu.restaurant.currency || "USD"} · Subject to availability`, marginX, pageH - 36);
    if (guestUrl) {
      doc.text(`Live menu: ${guestUrl}`, marginX, pageH - 24);
    }
    doc.text(`Page ${i} of ${pageCount}`, pageW - marginX - 50, pageH - 24);
  }

  const filename = `${(restaurantName || "menu")
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase()}-staff-menu.pdf`;
  doc.save(filename);
};

export default function ShareMenuPanel({ open, onClose, restaurantName }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  const guestUrl = user?.id
    ? `${window.location.origin}/menu/${user.id}`
    : "";

  // Generate QR code once the panel has a valid guestUrl
  useEffect(() => {
    if (!open || !guestUrl) return;
    let cancelled = false;
    QRCode.toDataURL(guestUrl, {
      margin: 1,
      width: 512,
      color: { dark: "#0f172a", light: "#ffffff" }, // slate-950 on white
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [open, guestUrl]);

  if (!open) return null;

  const whatsappMessage = t("shareMenu.whatsappMessage", {
    restaurant: restaurantName || t("shareMenu.emptyRestaurantName"),
    url: guestUrl,
  });
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      toast.success(t("toasts.menuLinkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("toasts.menuLinkCopyFailed"));
    }
  };

  const handleDownloadPdf = async () => {
    if (!user?.id) {
      toast.error(t("toasts.signInFirst"));
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/public/menu/${user.id}`);
      const menu = scrub(res.data);
      if (!menu.categories || menu.categories.length === 0) {
        toast.error(t("toasts.noActiveDishes"));
        return;
      }
      generateStaffPdf(menu, menu.restaurant.name || restaurantName, guestUrl, qrDataUrl, t);
      toast.success(t("toasts.pdfDownloaded"));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        toast.error(t("toasts.menuEmpty"));
      } else {
        toast.error(t("toasts.pdfFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = () => {
    // Force-open in a top-level new tab so the preview iframe's response-header
    // restrictions (ERR_BLOCKED_BY_RESPONSE) don't interfere with the navigation.
    const win = window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      // Popup blocked — fall back to navigating the top-level window itself.
      try {
        window.top.location.href = whatsappUrl;
      } catch {
        window.location.href = whatsappUrl;
      }
    }
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl) {
      toast.error(t("toasts.qrGenerating"));
      return;
    }
    const safeName = (restaurantName || "menu")
      .replace(/[^a-z0-9]+/gi, "-")
      .toLowerCase();
    const a = document.createElement("a");
    a.href = qrDataUrl;
    a.download = `${safeName}-menu-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(t("toasts.qrDownloaded"));
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-menu-panel-overlay"
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-menu-panel"
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-800">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Share2 className="h-5 w-5 text-amber-500" />
              {t("shareMenu.title")}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {t("shareMenu.subtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white"
            aria-label={t("common.close")}
            data-testid="share-menu-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Live link */}
          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">
              {t("shareMenu.liveUrlLabel")}
            </div>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 text-xs text-amber-400 font-mono bg-slate-950 px-3 py-2 rounded truncate"
                data-testid="share-menu-live-url"
              >
                {guestUrl || "—"}
              </code>
              <button
                type="button"
                onClick={() => {
                  const win = window.open(guestUrl, "_blank", "noopener,noreferrer");
                  if (!win) {
                    try {
                      window.top.location.href = guestUrl;
                    } catch {
                      window.location.href = guestUrl;
                    }
                  }
                }}
                className="p-2 text-slate-400 hover:text-amber-400"
                aria-label={t("shareMenu.openLinkAria")}
                data-testid="share-menu-open-link"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* QR code */}
          <div
            className="bg-slate-800/60 border border-slate-700 rounded-lg p-4"
            data-testid="share-menu-qr-block"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-24 h-24 bg-white rounded p-2 flex items-center justify-center">
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt=""
                    className="w-full h-full"
                    data-testid="share-menu-qr-image"
                  />
                ) : (
                  <QrCode className="h-8 w-8 text-slate-400 animate-pulse" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                  {t("shareMenu.scanToView")}
                </div>
                <div className="text-sm text-white font-medium leading-snug">
                  {t("shareMenu.scanCopy")}
                </div>
              </div>
            </div>
            <Button
              type="button"
              onClick={handleDownloadQr}
              disabled={!qrDataUrl}
              className="w-full mt-4 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-950 text-amber-400 border border-amber-500/40 hover:border-amber-400 font-semibold px-4 py-2 rounded-md transition-colors disabled:opacity-50"
              data-testid="share-menu-download-qr"
            >
              <FileDown className="h-4 w-4" />
              {t("shareMenu.downloadQr")}
            </Button>
          </div>

          {/* WhatsApp */}
          <Button
            type="button"
            onClick={handleShareWhatsApp}
            className="w-full flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1ea855] text-white font-semibold px-4 py-3 rounded-lg transition-colors"
            data-testid="share-menu-whatsapp"
          >
            <span className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5" />
              {t("shareMenu.shareWhatsapp")}
            </span>
            <span className="text-xs opacity-80">{t("shareMenu.whatsappHint")}</span>
          </Button>

          {/* Copy link */}
          <Button
            type="button"
            onClick={handleCopyLink}
            className="w-full flex items-center justify-between gap-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-4 py-3 rounded-lg transition-colors border border-slate-700"
            data-testid="share-menu-copy-link"
          >
            <span className="flex items-center gap-3">
              {copied ? (
                <Check className="h-5 w-5 text-emerald-400" />
              ) : (
                <LinkIcon className="h-5 w-5" />
              )}
              {copied ? t("shareMenu.copied") : t("shareMenu.copyLink")}
            </span>
          </Button>

          {/* Staff PDF */}
          <Button
            type="button"
            onClick={handleDownloadPdf}
            disabled={loading}
            className="w-full flex items-center justify-between gap-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-3 rounded-lg transition-colors disabled:opacity-60"
            data-testid="share-menu-download-pdf"
          >
            <span className="flex items-center gap-3">
              <FileDown className="h-5 w-5" />
              {loading ? t("shareMenu.preparingPdf") : t("shareMenu.downloadPdf")}
            </span>
            <span className="text-xs opacity-80">{t("shareMenu.pdfHint")}</span>
          </Button>

          <div className="text-xs text-slate-500 pt-2 border-t border-slate-800 leading-relaxed">
            {t("shareMenu.privacyFootnote")}
          </div>
        </div>
      </div>
    </div>
  );
}
